import OpenDialogButton from '@/components/Analysis/RiskAssessment/shared/OpenDialogButton';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { RiskTextField } from '../../Analysis/RiskAssessment/shared/styles';
import { BasicTabs } from '@/components/shared/BasicTabs';
import { FloatingLine, tabStyles } from './styles';

const riskFactorInputOptions = ['I', 'II', 'III', 'IV'];

type RiskFactorInputProps = {
  parentTab: 'pof' | 'cof';
  onInputChange: (selectedValues: { [key: string]: string | null }) => void;
};

const tabLabels: { [key: string]: string } = {
  risk_safety: 'Safety',
  risk_environment: 'Environment',
  risk_public_impact: 'Public Impact',
  risk_direct_cost: 'Direct Cost',
  risk_business_loss: 'Business Loss',
};

export const RiskFactorInputTabs = ({ parentTab, onInputChange }: RiskFactorInputProps) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [tabValue, setTabValue] = useState('risk_safety');
  const [selectedValues, setSelectedValues] = useState<{ [key: string]: string | null }>({
    risk_safety: '',
    risk_environment: '',
    risk_public_impact: '',
    risk_direct_cost: '',
    risk_business_loss: '',
  });

  useEffect(() => {
    if (parentTab === 'pof') {
      setSelectedValues({
        [`risk_safety_calculated_value_${parentTab}`]: '',
        [`risk_environment_calculated_value_${parentTab}`]: '',
        [`risk_public_impact_calculated_value_${parentTab}`]: '',
        [`risk_direct_cost_calculated_value_${parentTab}`]: '',
        [`risk_business_loss_calculated_value_${parentTab}`]: '',
      });
    }
    if (parentTab === 'cof') {
      setSelectedValues({
        [`risk_safety_${parentTab}`]: '',
        [`risk_environment_${parentTab}`]: '',
        [`risk_public_impact_${parentTab}`]: '',
        [`risk_direct_cost_${parentTab}`]: '',
        [`risk_business_loss_${parentTab}`]: '',
      });
    }
  }, [parentTab]);

  useEffect(() => {
    onInputChange(selectedValues);
  }, [onInputChange, selectedValues]);

  const handleDropdownChange = useCallback(
    (_event: React.SyntheticEvent<Element, Event>, dropdownValue: string | null) => {
      setSelectedValues((previousValues) => ({
        ...previousValues,
        [`${tabValue}_cof`]: dropdownValue ?? '',
      }));
    },
    [tabValue]
  );

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSelectedValues((previousValues) => ({
        ...previousValues,
        [`${tabValue}_calculated_value_pof`]: event.target.value || '',
      }));
    },
    [tabValue]
  );

  const getRiskFields = useCallback(
    (key: string) => {
      return (
        <>
          {parentTab === 'pof' && (
            <Stack direction="row" gap={3} alignItems="flex-end" width="100%" marginY={2}>
              <RiskTextField
                data-testid="pof-input-field"
                sx={{ width: '50%', mt: 2 }}
                value={selectedValues[`${key}_calculated_value_pof`] ?? ''}
                label="Value (C)"
                variant="standard"
                onChange={handleInputChange}
                error={
                  !/^(N\/A|\d*\.?\d*)$/.test(selectedValues[`${key}_calculated_value_pof`] ?? '')
                }
                helperText={
                  !/^(N\/A|\d*\.?\d*)$/.test(selectedValues[`${key}_calculated_value_pof`] ?? '')
                    ? 'Only numbers are allowed'
                    : ''
                }
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end"></InputAdornment>,
                  },
                }}
              />
              <OpenDialogButton
                location={parentTab.toUpperCase()}
                identifer={`${key}_value_pof_c`}
                title={`${tabLabels[key]}(POF)`}
              />
            </Stack>
          )}
          {parentTab === 'cof' && (
            <Stack direction="row" gap={3} alignItems="flex-end" width="100%" marginY={2}>
              <Autocomplete
                data-testid="cof-dropdown"
                sx={{ width: '100%', maxWidth: '30rem' }}
                value={selectedValues[`${key}_cof`] ?? ''}
                options={riskFactorInputOptions}
                getOptionLabel={(option) => option}
                isOptionEqualToValue={(option, value) => option === value}
                autoHighlight
                includeInputInList
                renderInput={(parameters) => (
                  <RiskTextField {...parameters} fullWidth label="Value (C)" variant="standard" />
                )}
                onChange={handleDropdownChange}
              />
              <OpenDialogButton
                location={parentTab.toUpperCase()}
                identifer={`${key}_cof`}
                title={`${tabLabels[key]}(COF)`}
              />
            </Stack>
          )}
        </>
      );
    },
    [handleDropdownChange, handleInputChange, parentTab, selectedValues]
  );

  const tabs = useMemo(() => {
    return Object.keys(tabLabels).map((key, index) => ({
      label: tabLabels[key],
      component: getRiskFields(key),
      index,
      key,
    }));
  }, [getRiskFields]);

  const handleTabSwitch = (index: number) => {
    const newTab = tabs.find((tab) => tab.index === index);
    setTabValue(newTab?.key ?? '');
    setTabIndex(index);
  };

  return (
    <>
      <Typography variant="caption">
        {`Risk factor (C) Note: Only the value 'C' entered in the field will be considered`}
      </Typography>
      <Box sx={{ width: '100%', border: '1px solid #DDDDDD' }}>
        <Box sx={{ padding: 3, paddingTop: '12px !important' }}>
          <BasicTabs
            renderingStrategy="eager"
            tabs={tabs}
            selectedTab={tabIndex}
            changeTab={handleTabSwitch}
            tabsStyles={tabStyles}
            customSeperator={<FloatingLine />}
          />
        </Box>
      </Box>
    </>
  );
};
